'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import SectionTitle from '@/components/atoms/SectionTitle';
import { trackPromotionClick } from '@/lib/client/gtm';
import UIButton from '../atoms/UIButtons/UIButton';
import PromotionDetails from './PromotionDetails';
import PromotionCard from './PromotionCard';
function sortPromotions(a, b) {
  const aExp = new Date(a.endDate || '').setHours(0, 0, 0, 0);
  const bExp = new Date(b.endDate || '').setHours(0, 0, 0, 0);
  if (bExp - aExp !== 0) return aExp - bExp;
  const aName = (a.title || '').toLowerCase();
  const bName = (b.title || '').toLowerCase();
  return aName.localeCompare(bName);
}

/**
 * Shared client component (island) that handles interactive promotion display
 * @param {Object} props
 * @param {Array<Object>} props.promotions - Array of promotion objects (full array or pre-sliced)
 * @param {string} props.title - Heading text for the promotions section
 * @param {string} props.communityName - Name of the community
 * @param {boolean} props.showViewAll - Whether to show "View All" button (optional, auto-calculated if limits provided)
 * @param {string} props.viewAllUrl - URL for "View All" button (optional)
 * @param {string} props.sectionId - Unique ID for the section (for accessibility)
 * @param {string} props.emptyStateMessage - Custom message for empty state
 * @param {number} props.desktopLimit - Maximum promotions to show on desktop (optional)
 * @param {number} props.mobileLimit - Maximum promotions to show on mobile (optional)
 * @returns {JSX.Element}
 */
export default function PromotionsSection(t0) {
  const $ = _c(22);
  const {
    promotions: t1,
    title,
    subTitle,
    communityName: t2,
    showViewAll: t3,
    viewAllUrl,
    emptyStateMessage: t4,
    desktopLimit,
    mobileLimit,
    source: t5,
    communityState: t6
  } = t0;
  const promotions = t1 === undefined ? [] : t1;
  const communityName = t2 === undefined ? "" : t2;
  const showViewAll = t3 === undefined ? false : t3;
  const emptyStateMessage = t4 === undefined ? "No promotions available at this time." : t4;
  const source = t5 === undefined ? "promotions_section" : t5;
  const communityState = t6 === undefined ? "" : t6;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  let t7;
  let t8;
  if ($[0] !== desktopLimit || $[1] !== mobileLimit) {
    t7 = () => {
      if (!desktopLimit && !mobileLimit) {
        return;
      }
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 1024);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    };
    t8 = [desktopLimit, mobileLimit];
    $[0] = desktopLimit;
    $[1] = mobileLimit;
    $[2] = t7;
    $[3] = t8;
  } else {
    t7 = $[2];
    t8 = $[3];
  }
  useEffect(t7, t8);
  const hasLimits = desktopLimit || mobileLimit;
  const displayLimit = hasLimits ? isMobile && mobileLimit ? mobileLimit : desktopLimit : null;
  const sortedPromotions = [...promotions].sort(sortPromotions);
  const displayedPromotions = displayLimit ? sortedPromotions.slice(0, displayLimit) : sortedPromotions;
  const shouldShowViewAll = hasLimits ? promotions.length > displayLimit : showViewAll;
  let t9;
  if ($[4] !== source) {
    t9 = item => {
      trackPromotionClick({
        promotionId: item.id,
        promotionName: item.offerText || item.title || item.name,
        promotionVendor: item.vendor,
        promotionUrl: item.url,
        source,
        view: "section"
      });
      setIsModalOpen(true);
      setSelectedItem(item);
    };
    $[4] = source;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const handleOpen = t9;
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsModalOpen(false);
      setSelectedItem(null);
    };
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const handleClose = t10;
  let t11;
  if ($[7] !== communityState || $[8] !== subTitle || $[9] !== title) {
    t11 = <div className="mb-5"><SectionTitle title={title} subTitle={subTitle} communityState={communityState} /></div>;
    $[7] = communityState;
    $[8] = subTitle;
    $[9] = title;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const t12 = displayedPromotions.length > 0 ? <div className="-mx-4 lg:mx-0">{displayedPromotions.map(item_0 => <PromotionCard key={item_0.id} promotion={item_0} onClick={() => handleOpen(item_0)} />)}{shouldShowViewAll && <div className="flex lg:justify-end justify-center mt-4"><UIButton href={viewAllUrl || "/broken"}>View All</UIButton></div>}</div> : <div className="py-4 px-4 lg:px-7 rounded-2xl bg-white shadow-[-2px_2px_20px_rgba(0,0,0,0.06)]" role="status" aria-live="polite">{emptyStateMessage}</div>;
  let t13;
  if ($[11] !== t11 || $[12] !== t12) {
    t13 = <div>{t11}{t12}</div>;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== communityName || $[15] !== handleClose || $[16] !== isModalOpen || $[17] !== selectedItem) {
    t14 = isModalOpen && <PromotionDetails isOpen={isModalOpen} onClose={handleClose} communityName={communityName || selectedItem?.communityName || ""} promotion={selectedItem} />;
    $[14] = communityName;
    $[15] = handleClose;
    $[16] = isModalOpen;
    $[17] = selectedItem;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t13 || $[20] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  return t15;
}